import "./SeloDeEmpresa.css";

/**
 * De qual empresa do grupo é esta linha.
 *
 * <h2>Por que ele existe</h2>
 *
 * Enquanto a tela mostrava <b>uma</b> empresa por vez, o CNPJ estava no
 * cabeçalho e a linha não precisava repeti-lo. A visão consolidada mudou isso:
 * dois lotes de CNPJs diferentes, um embaixo do outro, ficariam <b>visualmente
 * idênticos</b> — e material atribuído à empresa errada é documento inválido.
 *
 * <p>Por isso o selo não é enfeite: <b>é ele que torna o consolidado
 * aceitável.</b> Uma tabela que junta as empresas sem identificar cada linha é
 * pior que a tela que obrigava a escolher uma.
 *
 * <h2>Ele não tem cor, e isso é a decisão</h2>
 *
 * As duas famílias de {@code dominio.css} que já colorem a linha respondem
 * perguntas que decidem o uso do material — "este lote pode ser usado?" e "este
 * documento cobre esta operação?". A empresa não decide nenhuma das duas: ela
 * diz <b>de quem é</b>.
 *
 * <p>Dar-lhe um tom próprio a colocaria na mesma leitura de relance da coluna de
 * situação, e duas linguagens de cor na mesma linha não somam informação — elas
 * se confundem. Quem lê rápido passaria a decidir pela cor errada, justamente na
 * coluna que libera o material.
 *
 * <p>O que distingue uma empresa da outra é a <b>palavra</b>. O que distingue
 * este selo de um selo de status é a <b>forma</b>:
 *
 * <table>
 *   <tr><td>ação primária</td><td>verde sólido, texto branco, 44px de altura</td></tr>
 *   <tr><td>status</td><td>pílula, pastel + borda colorida, capitalizada</td></tr>
 *   <tr><td><b>empresa</b></td><td><b>canto reto, sem preenchimento, caixa alta com tracking</b></td></tr>
 * </table>
 *
 * <p>É a mesma separação por forma que o ADR-0004 §2.1 usa entre ação e status,
 * estendida a um terceiro papel.
 *
 * <h2>Sem empresa é DEFEITO, não célula vazia</h2>
 *
 * Se o nome não vier, o selo não desaparece nem sai em branco — ele grita, com a
 * família de perigo da camada semântica, que é de <b>defeito</b> e não de
 * domínio. Ausência de dado não aparece numa tela; estado nomeado, sim.
 *
 * <p>É inalcançável hoje: o servidor devolve os lotes filtrados pelo mesmo
 * conjunto de empresas de onde tira o nome de cada um. O caminho existe porque
 * "inalcançável" é uma afirmação sobre o código de hoje.
 */

interface Props {
  /** O apelido, como o servidor o devolve. Nulo ou vazio é defeito. */
  empresa: string | null;

  /**
   * A razão social, para o {@code title}.
   *
   * <p>O selo mostra o apelido porque a linha de tabela é estreita; quem
   * precisar do nome completo o alcança com o ponteiro. Opcional: no
   * consolidado nem sempre há razão social carregada, e o apelido sozinho já
   * identifica.
   */
  razaoSocial?: string;
}

export function SeloDeEmpresa({ empresa, razaoSocial }: Props) {
  const identificada = empresa !== null && empresa.trim() !== "";

  if (!identificada) {
    return (
      <span className="ui-selo-empresa ui-selo-empresa--ausente">sem empresa</span>
    );
  }

  return (
    <span className="ui-selo-empresa" title={razaoSocial}>
      {empresa}
    </span>
  );
}
